import React from 'react';
import { ChevronRight, TrendingUp } from 'lucide-react';
import { getMaturityCriteria, calculatePillarScore } from '../../config/cisaMaturityModel';

/**
 * PillarOverviewTab - Overview subtab showing pillar description and function breakdown
 */
const PillarOverviewTab = ({
  pillar,
  functions,
  assessments,
  targetLevels,
  onUpdateLevel,
  onUpdateTargetLevel
}) => {
  const LEVELS = ['TRADITIONAL', 'INITIAL', 'ADVANCED', 'OPTIMAL'];

  const formatLevel = (level) => level.charAt(0) + level.slice(1).toLowerCase();

  // Get pillar description
  const getPillarDescription = (code) => {
    const descriptions = {
      IDENTITY: 'Identity is the foundation of Zero Trust. It encompasses user authentication, identity stores, risk-based access decisions, and granular access management. Strong identity practices ensure only verified users access appropriate resources.',
      DEVICES: 'Device security ensures endpoints meet compliance requirements before accessing resources. This pillar covers policy enforcement, asset management, resource access controls, and threat protection.',
      NETWORKS: 'Network security in Zero Trust moves beyond perimeter-based models. It includes micro-segmentation, traffic management, encryption, and network resilience to limit lateral movement.',
      APPLICATIONS: 'Application security protects workloads through secure access, threat protection, accessibility, secure development, and continuous testing throughout the application lifecycle.',
      DATA: 'Data is the ultimate target in most attacks. This pillar ensures proper inventory, categorization, availability, access controls, and encryption of data at rest and in transit.'
    };
    return descriptions[code] || '';
  };

  // Calculate statistics
  const levelCounts = { TRADITIONAL: 0, INITIAL: 0, ADVANCED: 0, OPTIMAL: 0 };
  let gapsCount = 0;

  for (const fn of functions) {
    const currentLevel = assessments[fn.code] || 'TRADITIONAL';
    const targetLevel = targetLevels[fn.code];
    levelCounts[currentLevel]++;

    if (targetLevel && LEVELS.indexOf(targetLevel) > LEVELS.indexOf(currentLevel)) {
      gapsCount++;
    }
  }

  const pillarScore = calculatePillarScore(pillar.code, assessments);

  return (
    <div className="cisa-pillar-overview">
      {/* Pillar description */}
      <div className="cisa-pillar-overview-description">
        <h3>{pillar.name} Pillar</h3>
        <p>{getPillarDescription(pillar.code)}</p>
      </div>

      {/* Quick stats */}
      <div className="cisa-pillar-overview-stats">
        <div className="cisa-pillar-overview-stat">
          <span className="cisa-pillar-overview-stat-value">{pillarScore.toFixed(1)}</span>
          <span className="cisa-pillar-overview-stat-label">Pillar Score</span>
        </div>
        <div className="cisa-pillar-overview-stat">
          <span className="cisa-pillar-overview-stat-value">{functions.length}</span>
          <span className="cisa-pillar-overview-stat-label">Functions</span>
        </div>
        <div className="cisa-pillar-overview-stat">
          <span className="cisa-pillar-overview-stat-value">{gapsCount}</span>
          <span className="cisa-pillar-overview-stat-label">Active Gaps</span>
        </div>
      </div>

      {/* Level distribution */}
      <div className="cisa-pillar-overview-distribution">
        <h4>Maturity Distribution</h4>
        <div className="cisa-pillar-overview-levels">
          {LEVELS.map(level => {
            const count = levelCounts[level];
            const percentage = (count / functions.length) * 100;

            return (
              <div key={level} className="cisa-pillar-overview-level">
                <div className="cisa-pillar-overview-level-header">
                  <span className={`cisa-pillar-overview-level-name cisa-level--${level.toLowerCase()}`}>
                    {formatLevel(level)}
                  </span>
                  <span className="cisa-pillar-overview-level-count">{count}</span>
                </div>
                <div className="cisa-pillar-overview-level-bar">
                  <div
                    className={`cisa-pillar-overview-level-fill cisa-level--${level.toLowerCase()}`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Function breakdown */}
      <div className="cisa-pillar-overview-functions">
        <h4>Function Breakdown</h4>
        <div className="cisa-pillar-overview-function-list">
          {functions.map(fn => {
            const currentLevel = assessments[fn.code] || 'TRADITIONAL';
            const targetLevel = targetLevels[fn.code];
            const hasGap = targetLevel && LEVELS.indexOf(targetLevel) > LEVELS.indexOf(currentLevel);
            const criteria = getMaturityCriteria(fn.code, currentLevel);

            return (
              <div key={fn.code} className="cisa-pillar-overview-function">
                <div className="cisa-pillar-overview-function-header">
                  <div className="cisa-pillar-overview-function-info">
                    <span className="cisa-pillar-overview-function-name">{fn.name}</span>
                    <div className="cisa-pillar-overview-function-levels">
                      <span className={`cisa-level-badge cisa-level--${currentLevel.toLowerCase()}`}>
                        {formatLevel(currentLevel)}
                      </span>
                      {hasGap && (
                        <>
                          <ChevronRight size={14} className="cisa-pillar-overview-function-arrow" />
                          <span className={`cisa-level-badge cisa-level--${targetLevel.toLowerCase()}`}>
                            {formatLevel(targetLevel)}
                          </span>
                          <span className="cisa-pillar-overview-function-gap">
                            <TrendingUp size={12} />
                            Gap
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {criteria && (
                  <p className="cisa-pillar-overview-function-criteria">{criteria}</p>
                )}

                {/* Level selector */}
                <div className="cisa-pillar-overview-function-selector">
                  <span className="cisa-pillar-overview-function-selector-label">Current Level:</span>
                  <div className="cisa-pillar-overview-function-buttons">
                    {LEVELS.map(level => (
                      <button
                        key={level}
                        type="button"
                        className={`cisa-pillar-overview-level-btn ${currentLevel === level ? 'selected' : ''}`}
                        style={{
                          '--level-color': level === 'TRADITIONAL' ? '#6b7280' :
                            level === 'INITIAL' ? '#eab308' :
                              level === 'ADVANCED' ? '#3b82f6' : '#10b981'
                        }}
                        onClick={() => onUpdateLevel?.(fn.code, level)}
                      >
                        {level.charAt(0)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PillarOverviewTab;
